"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { t } from "@/lib/i18n/interpolate";
import {
  DEVANAGARI_SYSTEMS,
  type DevanagariOptions,
  type DevanagariSystem,
  devanagariLetterRows,
  latinToDevanagari,
  romanizeDevanagari,
} from "@/lib/tool-kit/transliteration-more/devanagari";

/** English strings. A translated content file overrides these through `ui`. */
export const DEVANAGARI_TRANSLITERATOR_STRINGS = {
  direction: "Direction",
  toLatin: "Devanagari → Latin",
  toDevanagari: "Latin → Devanagari",
  inputLabel: "Devanagari text",
  inputHint:
    "Hindi or Sanskrit, typed or pasted. Nukta letters, conjuncts with a virama, anusvāra, candrabindu and visarga are all read.",
  latinInputLabel: "Latin text",
  latinInputHint:
    "IAST or ISO 15919 with its diacritics: ā ī ū ṛ ṭ ḍ ṇ ś ṣ ṃ ḥ. Capital letters are fine.",
  system: "System to read",
  samples: {
    sanskrit: "भगवद्गीता",
    hindi: "हिंदी भाषा। मेरा नाम राम है।",
    iast: "bhagavadgītā",
    iso: "hindī bhāṣā. mērā nāma rāma hai.",
  },
  sampleSanskrit: "Sanskrit sample",
  sampleHindi: "Hindi sample",
  sampleLatin: "Insert a sample",
  optionsLegend: "Options",
  anusvaraLabel: "Anusvāra (ं)",
  anusvaraOptions: {
    mark: "Always ṃ (IAST) or ṁ (ISO 15919)",
    class: "Nasal of the next consonant: ṅ ñ ṇ n m",
  },
  finalALabel: "Inherent a at the end of a word",
  finalAOptions: {
    keep: "Keep it — strict transliteration (kamala)",
    drop: "Drop it — Hindi pronunciation (kamal)",
  },
  plainEO: "ISO 15919: write the long vowels ए and ओ as e and o, not ē and ō",
  capitalize: "Capitalize each word",
  finalConsonantLabel: "A word-final consonant with no vowel",
  finalConsonantOptions: {
    bare: "Leave it bare (Hindi: kamal → कमल)",
    halanta: "Add a virama (Sanskrit: kamal → कमल्)",
  },
  resultsHeading: "Romanized",
  devanagariHeading: "Devanagari",
  systems: {
    iast: "IAST",
    iso: "ISO 15919",
  },
  uses: {
    iast: "The Sanskrit scholars' standard. Vocalic ṛ, e and o for the long vowels, ṃ for anusvāra.",
    iso: "The international standard for Indic scripts. Vocalic r̥, ē and ō, ṁ for anusvāra, a tilde for candrabindu.",
  },
  devanagariUse: "Devanagari written from the {system} text above.",
  enterText: "Type or paste some text to convert.",
  unknownChars:
    "Left unchanged because the table does not include them: {chars}.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Letter by letter",
  letterColumn: "Devanagari",
  iastColumn: "IAST",
  isoColumn: "ISO 15919",
  groups: {
    vowel: "Vowels (independent form, then the sign on a consonant)",
    consonant: "Consonants",
    nukta: "Nukta consonants",
    sign: "Signs",
  },
  tableNote:
    "The IAST column writes the Hindi flap ड़ as ṛ, the same letter it uses for the vowel ऋ. The inherent a follows every consonant unless a vowel sign or a virama takes its place.",
} as const;

type Strings = typeof DEVANAGARI_TRANSLITERATOR_STRINGS;
type Direction = "to-latin" | "to-devanagari";

const DEFAULT_OPTIONS: DevanagariOptions = {
  anusvara: "mark",
  finalA: "keep",
  plainEO: false,
  capitalize: false,
  finalConsonant: "bare",
};

const GROUP_ORDER = ["vowel", "consonant", "nukta", "sign"] as const;

function LetterTable({ strings }: { strings: Strings }) {
  const rows = devanagariLetterRows();
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      {GROUP_ORDER.map((group) => (
        <div className="space-y-2" key={group}>
          <p className={LABEL}>{strings.groups[group]}</p>
          <DataTable
            head={[strings.letterColumn, strings.iastColumn, strings.isoColumn]}
            rows={rows
              .filter((row) => row.group === group)
              .map((row) => [row.dev, row.iast, row.iso])}
          />
        </div>
      ))}
      <p className={HINT}>{strings.tableNote}</p>
    </section>
  );
}

function Options({
  direction,
  options,
  setOptions,
  strings,
}: {
  direction: Direction;
  options: DevanagariOptions;
  setOptions: (update: (o: DevanagariOptions) => DevanagariOptions) => void;
  strings: Strings;
}) {
  return (
    <fieldset className="space-y-3">
      <legend className={LABEL}>{strings.optionsLegend}</legend>
      {direction === "to-latin" ? (
        <>
          <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
            <SelectField<DevanagariOptions["anusvara"]>
              id="dv-anusvara"
              label={strings.anusvaraLabel}
              onChange={(anusvara) => setOptions((o) => ({ ...o, anusvara }))}
              options={[
                { value: "mark", label: strings.anusvaraOptions.mark },
                { value: "class", label: strings.anusvaraOptions.class },
              ]}
              value={options.anusvara}
            />
            <SelectField<DevanagariOptions["finalA"]>
              id="dv-final-a"
              label={strings.finalALabel}
              onChange={(finalA) => setOptions((o) => ({ ...o, finalA }))}
              options={[
                { value: "keep", label: strings.finalAOptions.keep },
                { value: "drop", label: strings.finalAOptions.drop },
              ]}
              value={options.finalA}
            />
          </div>
          <Checkbox
            checked={options.plainEO}
            label={strings.plainEO}
            onChange={(plainEO) => setOptions((o) => ({ ...o, plainEO }))}
          />
          <Checkbox
            checked={options.capitalize}
            label={strings.capitalize}
            onChange={(capitalize) => setOptions((o) => ({ ...o, capitalize }))}
          />
        </>
      ) : (
        <div className="max-w-md">
          <SelectField<DevanagariOptions["finalConsonant"]>
            id="dv-final-consonant"
            label={strings.finalConsonantLabel}
            onChange={(finalConsonant) =>
              setOptions((o) => ({ ...o, finalConsonant }))
            }
            options={[
              { value: "bare", label: strings.finalConsonantOptions.bare },
              {
                value: "halanta",
                label: strings.finalConsonantOptions.halanta,
              },
            ]}
            value={options.finalConsonant}
          />
        </div>
      )}
    </fieldset>
  );
}

function ToLatinResults({
  text,
  options,
  strings,
}: {
  text: string;
  options: DevanagariOptions;
  strings: Strings;
}) {
  const results = DEVANAGARI_SYSTEMS.map((system) => ({
    system,
    result: romanizeDevanagari(text, system, options),
  }));
  const unknown = [...new Set(results.flatMap((each) => each.result.unknown))];
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {unknown.length > 0 ? (
        <Notice>{t(strings.unknownChars, { chars: unknown.join(" ") })}</Notice>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {results.map(({ system, result }) => (
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            key={system}
            lang="hi-Latn"
            text={result.text}
            title={strings.systems[system]}
            use={strings.uses[system]}
          />
        ))}
      </div>
    </section>
  );
}

function ToDevanagariResults({
  text,
  system,
  options,
  strings,
}: {
  text: string;
  system: DevanagariSystem;
  options: DevanagariOptions;
  strings: Strings;
}) {
  const result = latinToDevanagari(text, system, options);
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.devanagariHeading}</h2>
      {result.unknown.length > 0 ? (
        <Notice>
          {t(strings.unknownChars, { chars: result.unknown.join(" ") })}
        </Notice>
      ) : null}
      <ResultCard
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        lang="hi"
        text={result.text}
        title={strings.systems[system]}
        use={t(strings.devanagariUse, { system: strings.systems[system] })}
      />
    </section>
  );
}

export default function DevanagariTransliteratorWidget() {
  const strings = useToolUi(DEVANAGARI_TRANSLITERATOR_STRINGS);
  const [direction, setDirection] = useState<Direction>("to-latin");
  const [system, setSystem] = useState<DevanagariSystem>("iast");
  const [text, setText] = useState<string>(strings.samples.sanskrit);
  const [latin, setLatin] = useState<string>(strings.samples.iast);
  const [options, setOptions] = useState<DevanagariOptions>(DEFAULT_OPTIONS);
  const forward = direction === "to-latin";
  const current = forward ? text : latin;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="dv-direction"
        onChange={setDirection}
        options={[
          { value: "to-latin", label: strings.toLatin },
          { value: "to-devanagari", label: strings.toDevanagari },
        ]}
        value={direction}
      />
      {forward ? (
        <>
          <ScriptTextArea
            hint={strings.inputHint}
            id="dv-input"
            label={strings.inputLabel}
            lang="hi"
            onChange={setText}
            value={text}
          />
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => setText(strings.samples.sanskrit)}
              type="button"
            >
              {strings.sampleSanskrit}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() => setText(strings.samples.hindi)}
              type="button"
            >
              {strings.sampleHindi}
            </button>
          </div>
        </>
      ) : (
        <>
          <PillGroup
            label={strings.system}
            name="dv-system"
            onChange={setSystem}
            options={DEVANAGARI_SYSTEMS.map((each) => ({
              value: each,
              label: strings.systems[each],
            }))}
            value={system}
          />
          <ScriptTextArea
            dir="ltr"
            hint={strings.latinInputHint}
            id="dv-latin-input"
            label={strings.latinInputLabel}
            lang="sa-Latn"
            onChange={setLatin}
            value={latin}
          />
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => setLatin(strings.samples[system])}
              type="button"
            >
              {strings.sampleLatin}
            </button>
          </div>
        </>
      )}
      <Options
        direction={direction}
        options={options}
        setOptions={setOptions}
        strings={strings}
      />
      {current.trim() === "" ? <Notice>{strings.enterText}</Notice> : null}
      {current.trim() !== "" && forward ? (
        <ToLatinResults options={options} strings={strings} text={text} />
      ) : null}
      {current.trim() !== "" && !forward ? (
        <ToDevanagariResults
          options={options}
          strings={strings}
          system={system}
          text={latin}
        />
      ) : null}
      <LetterTable strings={strings} />
    </div>
  );
}
